import { SERVICE_HEAD as SERVICES } from "@/data/services-head.gen";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, collectionPageSchema, serviceWithReviewsSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
// /services — index hub listing all 6 flagship services
import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, ReviewBadge, TrustBar, ContactCTA, AeoAnswerBox, AtomicStats } from "@/components/seo/Blocks";

const SITE = "https://nyclanguageinstitute.com";
const META_TITLE = "บริการทั้งหมด — แปล Notary MFA สถานทูต NAATI Police Clearance | NYC Language Institute";
const META_DESC =
  "ศูนย์รวมบริการด้านเอกสารระหว่างประเทศครบวงจร — แปลรับรอง 50+ ภาษา · Notary Public · นิติกรณ์ MFA · รับรองสถานทูต · NAATI — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";

export const Route = createFileRoute("/services/")({
  loader: async () => {
    const { SERVICE_HEAD } = await import("@/data/services-head.gen");
    return { index: Object.values(SERVICE_HEAD).map((s) => ({ slug: s.slug, nameTh: s.nameTh })) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(META_TITLE, "/services", "th")},
      { name: "description", content: ctaDesc(META_DESC, "/services", "th")},
      { property: "og:title", content: ctaTitle(META_TITLE, "/services", "th")},
      { property: "og:description", content: ctaDesc(META_DESC, "/services", "th")},
      { property: "og:url", content: `${SITE}/services` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/services` }, ...hreflangLinks("/services")],
    scripts: [speakableSchema(), consolidateGraph([
      collectionPageSchema({ path: "/services", name: "บริการทั้งหมดของ NYC Legal & Notary Service", description: META_DESC, itemCount: (loaderData?.index ?? []).length }),
      serviceWithReviewsSchema({ path: "/services", serviceType: "Legal, Translation and Notary Services", name: META_TITLE, description: META_DESC, priceFromThb: 500 }),
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "บริการของ NYC Legal & Notary Service",
          itemListElement: (loaderData?.index ?? []).map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/services/${s.slug}`,
            name: s.nameTh,
          })),
        }),
      },
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
          ],
        }),
      },
    ])],
  }),
  component: ServicesIndex,
});

function ServicesIndex() {
  const services = Object.values(SERVICES);
  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      <section className="border-b border-white/10 px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl space-y-5">
          <Breadcrumbs items={[{ label: "หน้าแรก", to: "/" }, { label: "บริการทั้งหมด" }]} />
          <div className="flex flex-wrap items-center gap-2">
            <ReviewBadge />
            <span className="rounded-full bg-gold/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold">
              6 บริการเรือธง
            </span>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            บริการด้านเอกสารระหว่างประเทศ ครบวงจรในที่เดียว
          </h1>
          <p className="max-w-3xl text-base text-white/80 sm:text-lg">
            ตั้งแต่การแปลและรับรองคำแปล ไปจนถึงรับรองทนาย Notary Public รับรองนิติกรณ์กระทรวงการต่างประเทศ
            และรับรองที่สถานทูตของทุกประเทศ — เรารับผิดชอบทุกขั้นตอน เพื่อให้เอกสารของคุณใช้งานได้จริง
            ทั้งในประเทศและต่างประเทศ
          </p>
          <TrustBar />
          <ContactCTA />
        </div>
      </section>

      <div className="mx-auto max-w-5xl space-y-12 px-4 py-12 sm:px-6 sm:py-16">
        <AeoAnswerBox
          question="NYC Legal ให้บริการอะไรบ้าง?"
          answer="6 บริการเรือธง: แปลและรับรองเอกสาร 50+ ภาษา · Notary Public โดยทนายผู้รับรองลายมือชื่อ · NAATI Translation สำหรับออสเตรเลีย · ใบรับรองความประพฤติ (Police Clearance) · นิติกรณ์กระทรวงการต่างประเทศ (MFA) · รับรองเอกสารโดยสถานทูตทุกประเทศ — ครอบคลุม 50 เขต กทม. และ 77 จังหวัด เริ่ม ฿500"
        />

        <AtomicStats
          stats={[
            { label: "บริการเรือธง", value: "6 ด้าน" },
            { label: "ภาษา", value: "50+ ภาษา" },
            { label: "สาขา/ตัวแทน", value: "127 จุด" },
            { label: "ทนายผู้ทำคำรับรอง", value: "6 ท่าน" },
          ]}
        />

        <section>
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="text-2xl font-bold sm:text-3xl">รายการบริการทั้งหมด</h2>
            <span className="hidden text-xs uppercase tracking-widest text-gold/80 sm:inline">{services.length} บริการ</span>
          </div>
          <ul className="grid gap-4 md:grid-cols-6">
            {services.map((s, i) => {
              // First card is a featured 2-row 3-col bento cell
              const featured = i === 0;
              return (
                <li key={s.id} className={featured ? "md:col-span-3 md:row-span-2" : "md:col-span-2"}>
                  <Link
                    to="/services/$service"
                    params={{ service: s.slug }}
                    className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-5 transition hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[0_10px_40px_-10px_rgba(201,168,76,0.35)] ${featured ? "sm:p-7" : ""}`}
                  >
                    {featured && (
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,hsl(var(--gold)/0.25),transparent_60%)]" aria-hidden="true" />
                    )}
                    <div className="relative flex items-center gap-2">
                      <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold">
                        {featured ? "ยอดนิยม" : "บริการ"}
                      </span>
                      {i < 3 && !featured && (
                        <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold text-emerald-300">Top</span>
                      )}
                    </div>
                    <h3 className={`relative mt-2 font-bold text-white transition group-hover:text-gold ${featured ? "text-xl sm:text-2xl" : "text-lg"}`}>
                      {s.nameTh}
                    </h3>
                    <p className={`relative mt-1 text-white/80 ${featured ? "text-sm sm:text-base" : "text-sm"}`}>{s.shortTh}</p>
                    <div className="relative mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-[11px] text-white/80">
                      <span className="rounded bg-white/5 px-2 py-0.5 font-semibold text-gold">฿{s.priceFrom.toLocaleString()}+</span>
                      <span>{s.turnaround}</span>
                      <span className="ml-auto font-bold text-gold/80 transition group-hover:text-gold">ดูรายละเอียด →</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>


        <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/15 via-white/5 to-transparent p-6 sm:p-8">
          <h2 className="text-xl font-extrabold sm:text-2xl">ไม่แน่ใจว่าต้องใช้บริการไหน?</h2>
          <p className="mt-2 text-sm text-white/80">
            ส่งเอกสารและประเทศปลายทางมาให้ทีมงานประเมินฟรี — เราจะแนะนำลำดับขั้นตอนและค่าใช้จ่ายที่แม่นยำให้ภายใน 15 นาที
          </p>
          <div className="mt-5"><ContactCTA /></div>
        </section>
      </div>
          <SiteCoreLinks />
    </main>
  );
}
